import { createNarrowWidgetPositionLegacy } from '../common/schemas/legacy';

import type { AccordionEntity, LegacyAccordion } from './schemas';

type AccordionItem = LegacyAccordion['data']['accordionList'][number];

/**
 * Per-item host overrides — mirrors button's `CreateLegacyButtonOptions`: this package stays
 * dependency-free of i18n, so the host (`accordion.model.ts`) passes translated
 * `title`/`content` HTML per item. Three items with no override each get this bare placeholder.
 */
const DEFAULT_ITEM: AccordionItem = {
    title: 'Header',
    content: 'Text',
    isOpen: false,
    contentHeight: '0px',
};

export type CreateAccordionOptions = {
    accordionList?: readonly Partial<AccordionItem>[];
};

const createDefaultData = (options?: CreateAccordionOptions): LegacyAccordion['data'] => ({
    accordionList: (options?.accordionList ?? [{}, {}, {}]).map((item) => ({ ...DEFAULT_ITEM, ...item })),
    horizontalAlign: 'R',
    iconType: 'Caret',
    isIndent: false,
    themeIndentColor: null,
    themeIconColor: null,
    indentColor: '#333',
    iconColor: '#333',
    isAutoCollapse: true,
    isSynchronizeStyle: true,
    hideOnMobile: false,
    hideOnTablet: false,
    hideOnDesktop: false,
    position: createNarrowWidgetPositionLegacy(),
});

/**
 * Factory for the current legacy accordion entity — matches the persisted widget JSON shape:
 * `{ id, name: 'accordion', columnId, data, styles, mobileStyles }`. No variant argument:
 * accordion has a single placement (never zero-block).
 */
export function createLegacyAccordion(options?: CreateAccordionOptions): LegacyAccordion {
    return {
        id: '',
        name: 'accordion',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default for new accordion
 * widgets. While only `legacy` exists, this delegates to {@link createLegacyAccordion}.
 */
export function createAccordion(options?: CreateAccordionOptions): AccordionEntity {
    return createLegacyAccordion(options);
}
